import { Badge } from 'src/design/atoms/Badge';
import { Group } from 'src/design/atoms/Group';
import { MRT_ColumnDef } from 'mantine-react-table';
import React, { useMemo } from 'react';
import { ClassificationDto } from 'src/api/FiretrolApiClient';
import { useClassifications } from 'src/api/queries/organization/useClassifications';
import { useDialogs } from 'src/hooks/useDialogs';
import { EditActionIcon } from 'src/design/atoms/EditActionIcon';
import { CreateButton } from 'src/design/atoms/CreateButton';
import { Header } from 'src/design/organisms/Header';
import {
  useTable,
  useTableState,
} from 'src/design/organisms/DataTable/useTable';

const getColumns = (): MRT_ColumnDef<ClassificationDto>[] => {
  return [
    {
      accessorKey: 'name',
      header: 'Name',
    },
    {
      id: 'levels_count',
      header: 'Levels',
      accessorFn: (row) => row.levels?.length || 0,
      Cell: ({ cell }) => {
        const count = cell.getValue<number>();
        return (
          <Badge>
            {count} Level{count !== 1 ? 's' : ''}
          </Badge>
        );
      },
    },
  ];
};

export const Classifications: React.FC = () => {
  const { setAddClassificationOpen } = useDialogs();
  const { data, isLoading, isFetching } = useClassifications();
  const classifications = data ?? [];

  const columns = useMemo(() => {
    return getColumns();
  }, []);

  const tableState = useTableState({
    identifier: `classifications`,
    columns: columns,
    defaultSorting: [{ id: 'name', desc: false }],
  });

  const { table } = useTable({
    ...tableState,
    tableOptions: {
      enableRowActions: true,
      displayColumnDefOptions: { 'mrt-row-actions': { size: 150 } },
      renderRowActions: ({ row }) => {
        const classification = row.original;
        return (
          <Group>
            <EditActionIcon
              onClick={() => setAddClassificationOpen(true, classification)}
            />
          </Group>
        );
      },
      enableClickToCopy: false,
      columns: columns,
      data: classifications,
      manualPagination: false,
      manualSorting: false,
      manualFiltering: false,
      renderTopToolbarCustomActions: () => (
        <Group>
          <CreateButton onClick={() => setAddClassificationOpen(true)}>
            Add Classification
          </CreateButton>
        </Group>
      ),
    },
    getRowId: (row) => String(row.id),
    totalCount: classifications.length,
    isLoading,
    isFetching,
    isError: false,
  });

  return (
    <>
      <Header
        title="Classifications"
        breadcrumbProps={{
          items: [{ text: 'Settings' }, { text: 'Classifications' }],
        }}
        helpText="Classifications define job titles (e.g., Supervisor, Foreman) and their associated levels."
      />
      {table}
    </>
  );
};
